import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import DonutChart from "../../components/charts/DonutChart";
import Layout from "../../components/layout/Layout";
import {
  addPlaylistToScreen,
  screenFlagOff,
  uncheckAssignedScreen,
  updateScreenMemo,
  updateScreenName,
} from "../../Redux/Actions/screenAction";
import { getPlaylist } from "../../Redux/Actions/playlistAction";
import { getContent } from "../../Redux/Actions/contentAction";
import { api } from "../../api/api";
import toast from "react-hot-toast";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MemoryIcon from "@mui/icons-material/Memory";
import "./setting.css";

function Setting({ user, socketRef }) {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { screens, isUnChecked } = useSelector((state) => state.Screen);
  const { playlist } = useSelector((state) => state.Playlist);
  const { content: allContents } = useSelector((state) => state.Content);

  /** 이전/다음: screenSettingCarouselIds 우선, 없으면 예전 screenFilteredIds(전체 목록) */
  const navIds = (() => {
    try {
      const c = sessionStorage.getItem('screenSettingCarouselIds');
      if (c) {
        const a = JSON.parse(c);
        if (Array.isArray(a) && a.length) return a;
      }
      const l = sessionStorage.getItem('screenFilteredIds');
      if (l) {
        const a = JSON.parse(l);
        if (Array.isArray(a) && a.length) return a;
      }
    } catch (_) { /* ignore */ }
    return [];
  })();
  const currentIndex = navIds.indexOf(params.id);
  const prevId = currentIndex > 0 ? navIds[currentIndex - 1] : null;
  const nextId = currentIndex < navIds.length - 1 ? navIds[currentIndex + 1] : null;

  const [playlistInfo, setPlaylistInfo] = useState({});
  const [code, setCode] = useState("");
  const [openModel, setOpenModel] = useState(false);
  const [message, setMessage] = useState("");
  const [duration, setDuration] = useState(60);
  const [memo, setMemo] = useState("");
  const [memoSaved, setMemoSaved] = useState(false);
  const [openPlaylistDialog, setOpenPlaylistDialog] = useState(false);
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistSort, setPlaylistSort] = useState("name-asc");
  const [pendingPlaylistId, setPendingPlaylistId] = useState("");
  // 빠른 플레이리스트 생성 / 콘텐츠 추가
  const [quickCreateMode, setQuickCreateMode] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedExistingContents, setSelectedExistingContents] = useState([]);
  const [contentSearch, setContentSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [pingResult, setPingResult] = useState(null); // { connected, latency, ts }
  const [pinging, setPinging] = useState(false);
  const [deviceStats, setDeviceStats] = useState(null);  // 실시간 통계 { cpu, ram, storage, updatedAt }
  const [statsLoading, setStatsLoading] = useState(false);
  const [screenNameEdit, setScreenNameEdit] = useState("");
  const [nameSyncWithPlaylist, setNameSyncWithPlaylist] = useState(true);
  const [screenshotPreview, setScreenshotPreview] = useState(false);
  const [openNameSyncDialog, setOpenNameSyncDialog] = useState(false);
  const [pendingNameSave, setPendingNameSave] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);

  const nameEndsWithM = (n) => /[-_]m$/i.test((n || "").trim());

  useEffect(() => {
    const s = screens.find((s) => s._id === params.id);
    setPlaylistInfo({ ...s, screenAssigned: false });
    setMemo(s?.memo || "");
    setMemoSaved(false);
    setScreenshot(null);
    setDeviceStats(null);  // 페이지 전환 시 실시간 통계 초기화
    setStatsLoading(false);
    setScreenNameEdit(s?.name || "");
    const syncDefault = s?.nameSyncWithPlaylist !== false && !nameEndsWithM(s?.name);
    setNameSyncWithPlaylist(syncDefault);
  }, [params.id, screens]);

  useEffect(() => {
    const pl = playlistInfo?.playlist;
    if (pl == null) {
      setCode("");
      return;
    }
    const id = typeof pl === "object" && pl !== null ? pl._id : pl;
    setCode(id ? String(id) : "");
  }, [playlistInfo?.playlist]);

  useEffect(() => {
    if (isUnChecked) {
      setPlaylistInfo({ ...screens.find((s) => s._id === params.id) });
      dispatch(screenFlagOff());
    }
  }, [isUnChecked]);

  // Listen for screenshot from this device
  useEffect(() => {
    if (!socketRef?.current || !playlistInfo?.mac) return;
    const handler = ({ mac, image }) => {
      if (mac === playlistInfo.mac) {
        setScreenshot(image);
        setScreenshotLoading(false);
      }
    };
    socketRef.current.on('screenshot_received', handler);
    return () => socketRef.current?.off?.('screenshot_received', handler);
  }, [playlistInfo?.mac, socketRef]);

  // Listen for ping result
  useEffect(() => {
    if (!socketRef?.current) return;
    const handler = ({ mac, connected, latency, via }) => {
      if (mac === playlistInfo?.mac) {
        setPingResult({ connected, latency, via, ts: new Date().toLocaleTimeString() });
        setPinging(false);
      }
    };
    socketRef.current.on('ping_result', handler);
    return () => socketRef.current?.off?.('ping_result', handler);
  }, [playlistInfo?.mac, socketRef]);

  // 디바이스 리소스 통계 실시간 수신
  useEffect(() => {
    if (!socketRef?.current) return;
    const handler = ({ mac, cpu, ram, storage, updatedAt, offline }) => {
      if (mac !== playlistInfo?.mac) return;
      setStatsLoading(false);
      if (offline) return;
      setDeviceStats({ cpu, ram, storage, updatedAt });
    };
    socketRef.current.on('device_stats_update', handler);
    return () => socketRef.current?.off?.('device_stats_update', handler);
  }, [playlistInfo?.mac, socketRef]);

  const handleRequestScreenshot = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setScreenshotLoading(true);
    socketRef.current.emit('admin_request_screenshot', { mac: playlistInfo.mac });
  };

  const handleClearCache = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    socketRef.current.emit('admin_clear_cache', { mac: playlistInfo.mac });
  };

  const handleRequestStats = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setStatsLoading(true);
    socketRef.current.emit('admin_request_stats', { mac: playlistInfo.mac });
    // 10초 내 응답 없으면 timeout
    setTimeout(() => setStatsLoading(false), 10000);
  };

  const handlePingDevice = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setPinging(true);
    setPingResult(null);
    socketRef.current.emit('admin_ping_device', { mac: playlistInfo.mac, timestamp: Date.now() });
    // 5초 내 응답 없으면 timeout
    setTimeout(() => {
      setPinging(prev => {
        if (prev) setPingResult({ connected: false, latency: null, ts: new Date().toLocaleTimeString() });
        return false;
      });
    }, 5000);
  };

  const submit = () => {
    socketRef?.current.emit("message", {
      message,
      duration,
      mac: playlistInfo.mac,
    });
    setOpenModel(false);
    setMessage("");
  };

  const emitAssignPlaylistToScreen = (id) => {
    // backend가 AddPlaylistToScreen 성공 후 직접 device에 notify하므로
    // frontend는 1초 후 backup emit만 수행
    const screen = screens.find((s) => s._id === id);
    if (!screen) return;
    setTimeout(() => {
      socketRef.current?.emit("playlist_added", {
        detail: { mac: screen?.mac, code: id },
      });
    }, 1000);
  };

  const saveMemo = () => {
    dispatch(updateScreenMemo(params.id, memo));
    setMemoSaved(true);
    setTimeout(() => setMemoSaved(false), 2000);
  };

  const handleSaveScreenName = async () => {
    const newName = (screenNameEdit || "").trim();
    if (!newName) return;
    setNameSaving(true);
    try {
      await dispatch(updateScreenName(params.id, { name: newName, nameSyncWithPlaylist: false }));
    } finally {
      setNameSaving(false);
    }
  };

  const handleNameSyncDialogChoice = async (choice) => {
    if (!pendingNameSave) return;
    const { name } = pendingNameSave;
    if (choice === "unassign") {
      await dispatch(updateScreenName(params.id, { name, nameSyncWithPlaylist: false, removeAssignedPlaylist: true }));
    } else if (choice === "delete") {
      await dispatch(updateScreenName(params.id, { name, nameSyncWithPlaylist: false, removeAssignedPlaylist: true, deletePlaylist: true }));
      dispatch(getPlaylist(user?._id));
    }
    setOpenNameSyncDialog(false);
    setPendingNameSave(null);
  };

  const handleRemovePlaylist = async () => {
    if (!window.confirm('이 스크린에서 플레이리스트를 해제하시겠습니까?')) return;
    const currentName = (screenNameEdit || playlistInfo?.name || '').trim();
    if (!currentName) return;
    await dispatch(updateScreenName(params.id, { name: currentName, nameSyncWithPlaylist: false, removeAssignedPlaylist: true }));
    setCode(null);
    toast.success('플레이리스트 해제 완료');
  };

  const selectPlaylistById = (playlistId) => {
    setCode(playlistId);
    setOpenPlaylistDialog(false);
    setPlaylistSearch("");
    // 현재 스크린만 변경
    dispatch(addPlaylistToScreen({ id: params.id, value: playlistId }));
    emitAssignPlaylistToScreen(params.id);
  };

  const isOnline = playlistInfo?.status === true;

  // 실시간 통계 우선, 없으면 DB 값 사용
  const ram     = deviceStats?.ram     || playlistInfo?.ram;
  const storage = deviceStats?.storage || playlistInfo?.storage;
  const cpu     = deviceStats?.cpu     || playlistInfo?.cpu;
  const ramTotal  = ram?.total   ? +ram.total.split(" ")[0]   : 0;
  const ramFree   = ram?.free    ? +ram.free.split(" ")[0]    : 0;
  const storTotal = storage?.total ? +storage.total.split(" ")[0] : 0;
  const storFree  = storage?.free  ? +storage.free.split(" ")[0]  : 0;
  const cpuUsage  = cpu?.usage ? parseFloat(cpu.usage) : null;

  const infoRows = [
    { label: 'Device Name', value: playlistInfo?.device_details?.device_name },
    { label: 'Manufacturer',value: playlistInfo?.device_details?.manufacturer },
    { label: 'OS',          value: playlistInfo?.device_details?.os_version },
    { label: 'IP Address',  value: playlistInfo?.device_details?.ip },
    { label: 'MAC',         value: playlistInfo.mac },
    { label: 'Resolution',  value: playlistInfo?.device_details?.width ? `${playlistInfo.device_details.width} × ${playlistInfo.device_details.height}` : '-' },
    { label: 'Software',    value: playlistInfo?.device_details?.software },
    { label: 'Root Level',  value: playlistInfo?.device_details?.root_level },
  ];

  return (
    <>
      <Layout title={"Setting"} user={user}>
        <Grid container sx={{ px: 1, pt: 1 }}>

          {/* ── Left panel ── */}
          <Grid item lg={8} xs={12}>
            <Box sx={{ background: "white", p: 2.5, m: "14px 10px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>

              {/* Bulk nav */}
              {navIds.length > 0 && (
                <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'8px', pb:1, borderBottom:'1px solid rgba(85,31,255,0.1)', mb:1.5 }}>
                  <span style={{ fontSize:'12px', color:'#888' }}>
                    {currentIndex + 1} / {navIds.length}
                  </span>
                  {['이전', '다음'].map((label, i) => {
                    const targetId = i === 0 ? prevId : nextId;
                    return (
                      <button key={label} disabled={!targetId}
                        onClick={() => navigate(`/setting/${targetId}`)}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #551FFF', background: targetId ? '#551FFF' : '#eee', color: targetId ? 'white' : '#aaa', cursor: targetId ? 'pointer' : 'default', fontSize:'13px' }}
                      >
                        {i === 0 ? '◀ ' : ''}{label}{i === 1 ? ' ▶' : ''}
                      </button>
                    );
                  })}
                  <button onClick={() => navigate('/screen')} style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #ccc', background:'white', color:'#555', cursor:'pointer', fontSize:'13px' }}>목록</button>
                </Box>
              )}

              {/* 스크린 핵심: 이름 · 등록 코드 · 플레이리스트 */}
              <Box
                sx={{
                  mb: 2.5,
                  p: 2.5,
                  borderRadius: "14px",
                  background: "linear-gradient(145deg, rgba(85,31,255,0.07) 0%, rgba(255,255,255,0.96) 55%, rgba(85,31,255,0.03) 100%)",
                  border: "2px solid rgba(85,31,255,0.22)",
                  boxShadow: "0 10px 36px rgba(85,31,255,0.1)",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "#551FFF", fontWeight: 800, letterSpacing: "0.06em", mb: 2, textTransform: "uppercase" }}>
                  스크린 기본 정보
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 700, mb: 1 }}>스크린 이름</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                    <TextField
                      size="small"
                      value={screenNameEdit}
                      onChange={(e) => setScreenNameEdit(e.target.value)}
                      placeholder="스크린 이름"
                      fullWidth
                      sx={{
                        flex: "1 1 240px",
                        "& .MuiOutlinedInput-root": {
                          fontSize: 17,
                          fontWeight: 600,
                          bgcolor: "rgba(255,255,255,0.95)",
                        },
                      }}
                    />
                    {/* 플레이리스트 이름 연동 토글 — 비활성화 (2026-04-22) */}
                    <button
                      type="button"
                      onClick={handleSaveScreenName}
                      disabled={nameSaving || !(screenNameEdit || "").trim()}
                      style={{
                        padding: "11px 20px",
                        borderRadius: 10,
                        border: "none",
                        background: (screenNameEdit || "").trim() && !nameSaving ? "#551FFF" : "#d1d5db",
                        color: "white",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: (screenNameEdit || "").trim() && !nameSaving ? "pointer" : "default",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {nameSaving ? "저장 중..." : "이름 저장"}
                    </button>
                  </Box>
                  {nameEndsWithM(screenNameEdit) && (
                    <Typography sx={{ fontSize: 11, color: "#f57f17", mt: 1 }}>
                      이름이 -M / _M 으로 끝나 연동이 해제되며, 저장 시 기존 할당 플레이리스트 제거 여부를 선택할 수 있습니다.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "stretch" }}>
                  <Box
                    sx={{
                      flex: { md: "0 0 42%" },
                      minWidth: 0,
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: "rgba(255,255,255,0.92)",
                      border: "1px solid rgba(85,31,255,0.18)",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      등록 코드
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: { xs: 18, sm: 22 },
                          fontWeight: 800,
                          color: "#111827",
                          letterSpacing: "0.06em",
                          wordBreak: "break-all",
                          lineHeight: 1.25,
                        }}
                      >
                        {playlistInfo.code || "—"}
                      </Typography>
                      {playlistInfo.code && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(String(playlistInfo.code));
                            toast.success("코드를 복사했습니다");
                          }}
                          style={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "7px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#551FFF",
                            background: "rgba(85,31,255,0.1)",
                            border: "1px solid rgba(85,31,255,0.28)",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                          복사
                        </button>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 1.2, lineHeight: 1.45 }}>
                      앱·기기 등록 시 입력하는 코드입니다.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: "rgba(255,255,255,0.92)",
                      border: "1px solid rgba(85,31,255,0.18)",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      플레이리스트
                    </Typography>
                    {playlistInfo?.playlist === undefined || playlistInfo?.screenAssigned ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenPlaylistDialog(true);
                            setPlaylistSearch("");
                          }}
                          style={{
                            width: "100%",
                            minHeight: 52,
                            padding: "12px 14px",
                            background: code ? "rgba(85,31,255,0.14)" : "rgba(243,244,246,0.98)",
                            border: code ? "2px solid rgba(85,31,255,0.45)" : "2px dashed rgba(85,31,255,0.38)",
                            borderRadius: "10px",
                            cursor: "pointer",
                            fontSize: 16,
                            fontWeight: 700,
                            color: code ? "#1e1b4b" : "#9ca3af",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {code
                              ? playlist?.find((p) => String(p._id) === String(code))?.name || "선택됨"
                              : "플레이리스트를 선택하세요"}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#551FFF", flexShrink: 0 }}>변경 ▾</span>
                        </button>
                        {code && (
                          <button
                            type="button"
                            onClick={handleRemovePlaylist}
                            style={{
                              marginTop: 6,
                              width: "100%",
                              padding: "8px 14px",
                              background: "white",
                              border: "1px solid #e53935",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#e53935",
                              textAlign: "center",
                            }}
                          >
                            플레이리스트 해제
                          </button>
                        )}
                      </>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", minHeight: 52 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#15192C" }}>{playlistInfo?.playlist?.name}</Typography>
                        <BorderColorIcon
                          style={{ color: "#6179FB", cursor: "pointer", fontSize: 22 }}
                          onClick={() => dispatch(uncheckAssignedScreen(playlistInfo._id))}
                        />
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 1.2, lineHeight: 1.45 }}>
                      이 스크린에서 재생할 목록입니다. 다른 스크린에는 영향 없습니다.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 2, mb: 1, borderBottom: "1px solid rgba(85,31,255,0.12)" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "rgba(85,31,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <img src="/images/MobileScreen.png" alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#15192C", lineHeight: 1.3 }}>
                    {playlistInfo?.device_details?.device_name || "디바이스"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: isOnline ? "#22c55e" : "#d1d5db" }} />
                    <Typography sx={{ fontSize: 12, color: isOnline ? "#22c55e" : "#9ca3af" }}>{isOnline ? "Connected" : "Disconnected"}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Info grid */}
              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, borderRadius:'10px', overflow:'hidden', border:'1px solid rgba(85,31,255,0.12)', mb:2 }}>
                {infoRows.map(({ label, value }, i) => (
                  <Box key={label} sx={{ px:2.5, py:1.5, background: i % 4 < 2 ? 'rgba(85,31,255,0.02)' : 'white', borderBottom: i < infoRows.length - 2 ? '1px solid rgba(85,31,255,0.08)' : 'none', borderRight: i % 2 === 0 ? '1px solid rgba(85,31,255,0.08)' : 'none' }}>
                    <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', mb:0.3 }}>{label}</Typography>
                    <Typography sx={{ fontSize:13, color:'#15192C', fontWeight:500 }}>{value || '-'}</Typography>
                  </Box>
                ))}
              </Box>

              {/* 리소스 통계 (RAM / Storage / CPU) */}
              {(ram?.total || storage?.total || cpuUsage !== null) && (
                <Box sx={{ mb:2 }}>
                  {/* 헤더: 제목 + 마지막 업데이트 시각 */}
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                    <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                      디바이스 리소스
                    </Typography>
                    {deviceStats?.updatedAt && (
                      <Typography sx={{ fontSize:10, color:'#bbb' }}>
                        {new Date(deviceStats.updatedAt).toLocaleTimeString()} 기준
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display:'flex', gap:2, justifyContent:'center', flexWrap:'wrap' }}>
                    {ram?.total && (
                      <DonutChart views="Ram"
                        series={[ramTotal - ramFree, ramFree]}
                        labels={[`Usage ${ram.total?.split(" ")[1] || ''}`, `Free ${ram.free?.split(" ")[1] || ''}`]}
                      />
                    )}
                    {storage?.total && (
                      <DonutChart views="Storage"
                        series={[storTotal - storFree, storFree]}
                        labels={[`Usage ${storage.total?.split(" ")[1] || ''}`, `Free ${storage.free?.split(" ")[1] || ''}`]}
                      />
                    )}
                    {cpuUsage !== null && (
                      <DonutChart views="CPU"
                        series={[Math.round(cpuUsage), Math.round(100 - cpuUsage)]}
                        labels={[`사용 ${Math.round(cpuUsage)}%`, `여유 ${Math.round(100 - cpuUsage)}%`]}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Memo */}
              <Box sx={{ borderTop:'1px solid rgba(85,31,255,0.1)', pt:2 }}>
                <Typography sx={{ fontSize:12, color:'#92959E', fontWeight:600, mb:0.8 }}>MEMO</Typography>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  rows={3}
                  style={{ width:"100%", border:"1px solid rgba(85,31,255,0.2)", borderRadius:"8px", padding:"10px", fontSize:"13px", resize:"vertical", fontFamily:"inherit", outline:"none", boxSizing:'border-box' }}
                  placeholder="메모를 입력하세요..."
                />
                <Box sx={{ display:"flex", justifyContent:"flex-end", mt:0.8, gap:1, alignItems:"center" }}>
                  {memoSaved && <span style={{ fontSize:"12px", color:"#551FFF" }}>저장됨</span>}
                  <button onClick={saveMemo} style={{ background:"#551FFF", color:"white", border:"none", borderRadius:"6px", padding:"6px 18px", fontSize:"13px", cursor:"pointer" }}>저장</button>
                </Box>
              </Box>

            </Box>
          </Grid>

          {/* ── Right panel ── */}
          <Grid item lg={4} xs={12}>
            {/* QuickBooks 연동 설정 — 숨김 (2026-04-22) */}

            <Box sx={{ background:"white", m:"14px 10px", borderRadius:"16px", boxShadow:"0 4px 20px rgba(0,0,0,0.06)", overflow:'hidden' }}>

              {/* Panel header */}
              <Box sx={{ background:'#551FFF', px:2.5, py:1.5, display:'flex', alignItems:'center', gap:1 }}>
                <CameraAltIcon sx={{ color:'white', fontSize:18 }} />
                <Typography sx={{ color:'white', fontWeight:700, fontSize:14 }}>화면 미리보기</Typography>
              </Box>

              {/* Screenshot area */}
              <Box sx={{ background:'#1a1a2e', minHeight:220, display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                {screenshotLoading && (
                  <Box sx={{ textAlign:'center', color:'#aaa' }}>
                    <Typography sx={{ fontSize:13 }}>캡처 중...</Typography>
                  </Box>
                )}
                {!screenshotLoading && screenshot && (
                  <img src={`data:image/png;base64,${screenshot}`} alt="screenshot"
                    onClick={() => setScreenshotPreview(true)}
                    style={{ maxWidth:'100%', maxHeight:280, objectFit:'contain', display:'block', cursor:'zoom-in' }} />
                )}
                {!screenshotLoading && !screenshot && (
                  <Box sx={{ textAlign:'center', color:'#555' }}>
                    <CameraAltIcon sx={{ fontSize:40, mb:1 }} />
                    <Typography sx={{ fontSize:12 }}>스크린샷 없음</Typography>
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              <Box sx={{ p:2, display:'flex', flexDirection:'column', gap:1.2 }}>

                {/* 연결 상태 체크 */}
                <button
                  onClick={handlePingDevice}
                  disabled={pinging}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #22c55e', background:'white', color: pinging ? '#9ca3af' : '#16a34a', cursor: pinging ? 'not-allowed' : 'pointer', fontSize:'13px', fontWeight:600 }}
                >
                  <span style={{ fontSize:14 }}>📡</span>
                  {pinging ? '응답 대기 중...' : '연결 상태 확인'}
                </button>

                {/* Ping 결과 표시 */}
                {pingResult && (
                  <Box sx={{ px:1.5, py:1, borderRadius:'8px', border:'1px solid', borderColor: pingResult.connected ? '#bbf7d0' : '#fecaca', background: pingResult.connected ? '#f0fdf4' : '#fef2f2', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', background: pingResult.connected ? '#22c55e' : '#ef4444' }} />
                      <Typography sx={{ fontSize:12, fontWeight:600, color: pingResult.connected ? '#16a34a' : '#dc2626' }}>
                        {pingResult.connected ? (pingResult.latency != null ? `응답 완료 (${pingResult.latency}ms)` : '연결됨 (소켓 접속 중)') : '응답 없음 (오프라인)'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize:11, color:'#9ca3af' }}>{pingResult.ts}</Typography>
                  </Box>
                )}

                <button
                  onClick={handleRequestScreenshot}
                  disabled={!isOnline || screenshotLoading}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'none', background: isOnline ? '#551FFF' : '#e5e7eb', color: isOnline ? 'white' : '#9ca3af', cursor: isOnline ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <CameraAltIcon sx={{ fontSize:16 }} />
                  {screenshotLoading ? '캡처 중...' : '스크린샷 요청'}
                </button>

                <button
                  onClick={() => setOpenModel(true)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #551FFF', background:'white', color:'#551FFF', cursor:'pointer', fontSize:'13px', fontWeight:600 }}
                >
                  <MailOutlineIcon sx={{ fontSize:16 }} />
                  메시지 전송 (흐르는 자막)
                </button>

                <button
                  onClick={handleClearCache}
                  disabled={!isOnline}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #f59e0b', background:'white', color: isOnline ? '#f59e0b' : '#9ca3af', cursor: isOnline ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <DeleteSweepIcon sx={{ fontSize:16 }} />
                  캐시 초기화
                </button>

                {/* 리소스 조회 버튼 */}
                <button
                  onClick={handleRequestStats}
                  disabled={!isOnline || statsLoading}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #8b5cf6', background:'white', color: (isOnline && !statsLoading) ? '#8b5cf6' : '#9ca3af', cursor: (isOnline && !statsLoading) ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <MemoryIcon sx={{ fontSize:16 }} />
                  {statsLoading ? '조회 중...' : '리소스 조회'}
                </button>

                {/* 리소스 최신 수신 시각 표시 */}
                {deviceStats?.updatedAt && !statsLoading && (
                  <Box sx={{ px:1.5, py:0.8, borderRadius:'8px', border:'1px solid #ede9fe', background:'#faf5ff', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Typography sx={{ fontSize:11, color:'#7c3aed', fontWeight:600 }}>CPU {cpuUsage !== null ? `${Math.round(cpuUsage)}%` : '-'}</Typography>
                    <Typography sx={{ fontSize:10, color:'#aaa' }}>{new Date(deviceStats.updatedAt).toLocaleTimeString()} 기준</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Layout>

      {/* Send Message dialog */}
      <Dialog fullWidth open={openModel} onClose={() => setOpenModel(false)}>
        <DialogTitle style={{ textAlign:"center", borderBottom:"2px solid #551fff", fontSize:"18px", fontWeight:700 }}>
          메시지 전송 (흐르는 자막)
        </DialogTitle>
        <DialogContent>
          <div className="screen-form">
            <div className="icon-screen">
              <MailOutlineIcon sx={{ color:"#551fff", height:"50px", width:"50px" }} />
            </div>
            <Box sx={{ mb:1.5, px:0.5, py:1, background:'rgba(85,31,255,0.04)', borderRadius:'8px', border:'1px solid rgba(85,31,255,0.12)' }}>
              <Typography sx={{ fontSize:12, color:'#551FFF', textAlign:'center' }}>
                📺 앱 하단에 흐르는 자막(marquee)으로 표시됩니다
              </Typography>
              <Typography sx={{ fontSize:11, color:'#92959E', textAlign:'center', mt:0.3 }}>
                소켓 이벤트: <code>message_recieved</code> → tickerText
              </Typography>
            </Box>
            <div className="form-fields">
              <TextField type="text" value={message} onChange={(e) => setMessage(e.target.value)} variant="outlined" label="메시지 내용" multiline fullWidth placeholder="화면 하단에 표시할 메시지를 입력하세요" />
            </div>
            <div className="form-fields" style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
              <TextField
                type="number" value={duration}
                onChange={(e) => setDuration(e.target.value)}
                label="표시 시간(초)"
                helperText="앱에서 ticker 표시 시간"
                sx={{ width:"160px" }}
                inputProps={{ min:1, max:300 }}
              />
            </div>
            <Box sx={{ display:"flex", mt:4, mb:3, justifyContent:"center", gap:2 }}>
              <button style={{ background:"white", padding:"8px", width:"130px", color:"#551FFF", border:"1px solid #551FFF", borderRadius:"5px", cursor:"pointer" }} onClick={() => setOpenModel(false)}>취소</button>
              <button style={{ background: message.trim() ? "#551FFF" : "#ccc", padding:"8px", width:"130px", color:"white", border:"none", borderRadius:"5px", cursor: message.trim() ? "pointer" : "default" }} onClick={submit} disabled={!message.trim()}>전송</button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      {/* Playlist select dialog */}
      <Dialog fullWidth open={openPlaylistDialog} onClose={() => { setOpenPlaylistDialog(false); setPlaylistSearch(""); }}>
        <DialogTitle style={{ textAlign:'center', borderBottom:'2px solid #551fff', fontSize:'18px', fontWeight:700 }}>
          플레이리스트 선택 (현재 스크린만)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py:2, display:'flex', flexDirection:'column', gap:1.5 }}>
            <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
              <input
                value={playlistSearch}
                onChange={e => setPlaylistSearch(e.target.value)}
                placeholder="이름 검색..."
                style={{ flex:1, height:'36px', border:'1px solid rgba(85,31,255,0.3)', borderRadius:'8px', padding:'0 10px', fontSize:'13px', outline:'none' }}
              />
              <select
                value={playlistSort}
                onChange={e => setPlaylistSort(e.target.value)}
                style={{ height:'36px', border:'1px solid rgba(85,31,255,0.3)', borderRadius:'8px', padding:'0 8px', fontSize:'12px', color:'#555' }}
              >
                <option value="name-asc">이름 A→Z</option>
                <option value="name-desc">이름 Z→A</option>
                <option value="date-desc">최신순</option>
                <option value="date-asc">오래된순</option>
              </select>
            </Box>
            <Box sx={{ maxHeight:'300px', overflowY:'auto', border:'1px solid rgba(85,31,255,0.15)', borderRadius:'8px' }}>
              {(() => {
                const searched = (playlist || []).filter(p => p.name?.toLowerCase().includes(playlistSearch.toLowerCase()));
                const sorted = [...searched].sort((a, b) => {
                  if (playlistSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
                  if (playlistSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
                  if (playlistSort === 'date-desc') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                  return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
                });
                if (sorted.length === 0 && !quickCreateMode) return (
                  <Box sx={{ textAlign:'center', py:3, px:2 }}>
                    <p style={{ color:'#aaa', fontSize:'13px', margin:'0 0 12px' }}>결과 없음</p>
                    <button onClick={() => { setQuickCreateMode(true); setNewPlaylistName(screens?.find(s => s._id === params.id)?.name || ''); }}
                      style={{ background:'#551FFF', color:'white', border:'none', borderRadius:'6px', padding:'8px 20px', cursor:'pointer', fontSize:'13px', fontWeight:600 }}>
                      + 새 플레이리스트 만들기
                    </button>
                  </Box>
                );
                if (sorted.length === 0 && quickCreateMode) return null;
                return sorted.map(p => {
                  const hasContent = p.content?.length > 0;
                  return (
                  <Box key={p._id} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.5, cursor:'pointer', borderBottom:'1px solid rgba(0,0,0,0.05)',
                    background: pendingPlaylistId === p._id ? 'rgba(85,31,255,0.08)' : hasContent ? 'rgba(34,197,94,0.04)' : 'white',
                    borderLeft: hasContent ? '3px solid #22c55e' : '3px solid transparent',
                    '&:hover': { background:'rgba(85,31,255,0.05)' } }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1, flex:1 }} onClick={() => setPendingPlaylistId(p._id)}>
                      <Box sx={{ width:10, height:10, borderRadius:'50%', border:`2px solid ${hasContent ? '#22c55e' : '#551FFF'}`, background: pendingPlaylistId === p._id ? (hasContent ? '#22c55e' : '#551FFF') : 'white', flexShrink:0 }} />
                      <span style={{ fontSize:'14px', color:'#15192C', fontWeight: hasContent ? 600 : 400 }}>{p.name}</span>
                    </Box>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <span style={{ fontSize:'11px', color: hasContent ? '#22c55e' : '#aaa', fontWeight: hasContent ? 600 : 400 }}>{p.content?.length || 0}개</span>
                      <button onClick={(e) => { e.stopPropagation(); setQuickCreateMode(true); setNewPlaylistName(p.name); setPendingPlaylistId(p._id); setSelectedExistingContents([]); setUploadFiles([]); }}
                        style={{ fontSize:'10px', padding:'2px 8px', border:`1px solid ${p.content?.length ? '#22c55e' : '#551FFF'}`, borderRadius:'4px', background:'white', color: p.content?.length ? '#22c55e' : '#551FFF', cursor:'pointer', whiteSpace:'nowrap' }}>
                        + 콘텐츠
                      </button>
                    </Box>
                  </Box>
                  );
                })
              })()}
            </Box>
            {/* 콘텐츠 추가 / 새 플레이리스트 생성 폼 */}
            {quickCreateMode && (
              <Box sx={{ border:'2px dashed #551FFF', borderRadius:'8px', p:2, mt:1, background:'rgba(85,31,255,0.03)' }}>
                <p style={{ fontSize:'14px', fontWeight:700, color:'#551FFF', margin:'0 0 8px' }}>
                  {pendingPlaylistId ? '콘텐츠 추가' : '새 플레이리스트 만들기'}
                </p>

                {/* 플레이리스트 이름 (새 생성 시에만) */}
                {!pendingPlaylistId && (
                  <input value={newPlaylistName} onChange={e => setNewPlaylistName(e.target.value)}
                    placeholder="플레이리스트 이름"
                    style={{ width:'100%', height:'36px', border:'1px solid rgba(85,31,255,0.3)', borderRadius:'8px', padding:'0 10px', fontSize:'13px', outline:'none', marginBottom:'10px', boxSizing:'border-box' }} />
                )}

                {/* 2단 레이아웃: 왼쪽 드래그앤드롭 | 오른쪽 기존 콘텐츠 */}
                <Box sx={{ display:'flex', gap:1.5, minHeight:'160px' }}>

                  {/* 왼쪽: 드래그앤드롭 업로드 */}
                  <Box sx={{ flex:1, border: dragOver ? '2px solid #551FFF' : '1px dashed #ccc', borderRadius:'8px', p:1.5,
                    textAlign:'center', cursor:'pointer', transition:'border 0.2s', background: dragOver ? 'rgba(85,31,255,0.05)' : 'white' }}
                    onClick={() => document.getElementById('quick-upload-input')?.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); setUploadFiles([...uploadFiles, ...Array.from(e.dataTransfer.files)]); }}>
                    <input id="quick-upload-input" type="file" accept="image/*,video/*" multiple hidden
                      onChange={e => { setUploadFiles([...uploadFiles, ...Array.from(e.target.files)]); e.target.value=''; }} />
                    <p style={{ fontSize:'11px', color:'#551FFF', fontWeight:600, margin:'0 0 6px' }}>새 파일 업로드</p>
                    {uploadFiles.length === 0 ? (
                      <p style={{ color:'#aaa', fontSize:'11px', margin:0, lineHeight:1.6 }}>클릭 또는<br/>드래그앤드롭</p>
                    ) : (
                      <Box sx={{ display:'flex', flexDirection:'column', gap:0.5, alignItems:'center' }}>
                        {uploadFiles.map((f, i) => (
                          <Box key={i} sx={{ display:'flex', alignItems:'center', gap:0.5, background:'rgba(85,31,255,0.08)', borderRadius:'4px', px:1, py:0.3, fontSize:'10px', maxWidth:'100%' }}>
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                            <span onClick={e => { e.stopPropagation(); setUploadFiles(uploadFiles.filter((_,j)=>j!==i)); }}
                              style={{ cursor:'pointer', color:'#f44', fontWeight:700, flexShrink:0 }}>×</span>
                          </Box>
                        ))}
                        <span style={{ fontSize:'10px', color:'#551FFF' }}>+ 추가</span>
                      </Box>
                    )}
                  </Box>

                  {/* 오른쪽: 기존 콘텐츠 선택 */}
                  <Box sx={{ flex:1, border:'1px solid #eee', borderRadius:'8px', overflow:'hidden', display:'flex', flexDirection:'column' }}>
                    <Box sx={{ px:1, py:0.5, borderBottom:'1px solid #eee', background:'#fafafa' }}>
                      <input value={contentSearch} onChange={e => setContentSearch(e.target.value)}
                        placeholder="기존 콘텐츠 검색..."
                        style={{ width:'100%', height:'28px', border:'none', fontSize:'11px', outline:'none', background:'transparent', boxSizing:'border-box' }} />
                    </Box>
                    <Box sx={{ flex:1, overflowY:'auto', maxHeight:'140px' }}>
                      {(() => {
                        const seen = new Set();
                        return (allContents || []).filter(c => {
                          if (seen.has(c._id)) return false;
                          seen.add(c._id);
                          return c.name?.toLowerCase().includes(contentSearch.toLowerCase());
                        }).slice(0, 30);
                      })().map(c => {
                          const isSelected = selectedExistingContents.some(s => s._id === c._id);
                          return (
                            <Box key={c._id} onClick={(e) => {
                              e.stopPropagation();
                              if (isSelected) setSelectedExistingContents(prev => prev.filter(s => s._id !== c._id));
                              else setSelectedExistingContents(prev => [...prev, c]);
                            }}
                            sx={{ display:'flex', alignItems:'center', gap:1, px:1, py:1, cursor:'pointer', fontSize:'12px',
                              borderBottom:'1px solid #f5f5f5', background: isSelected ? 'rgba(85,31,255,0.08)' : 'white',
                              '&:hover':{ background:'rgba(85,31,255,0.04)' }, userSelect:'none' }}>
                              <Box sx={{ width:14, height:14, borderRadius:'3px', border:`2px solid ${isSelected ? '#551FFF' : '#ccc'}`, flexShrink:0,
                                background: isSelected ? '#551FFF' : 'white', display:'flex', alignItems:'center', justifyContent:'center',
                                transition:'0.15s' }}>
                                {isSelected && <span style={{ color:'white', fontSize:10, fontWeight:700 }}>✓</span>}
                              </Box>
                              <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</span>
                              <span style={{ color:'#aaa', fontSize:'9px', flexShrink:0 }}>{c.type}</span>
                            </Box>
                          );
                        })}
                      {(!allContents || allContents.length === 0) && (
                        <p style={{ textAlign:'center', color:'#aaa', fontSize:'11px', padding:'16px' }}>콘텐츠 없음</p>
                      )}
                    </Box>
                    {selectedExistingContents.length > 0 && (
                      <Box sx={{ px:1, py:0.5, borderTop:'1px solid #eee', background:'rgba(85,31,255,0.04)', fontSize:'10px', color:'#551FFF', fontWeight:600 }}>
                        {selectedExistingContents.length}개 선택됨
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 액션 버튼 */}
                <Box sx={{ display:'flex', gap:1, mt:1.5, justifyContent:'center' }}>
                  <button onClick={() => { setQuickCreateMode(false); setUploadFiles([]); setNewPlaylistName(""); setSelectedExistingContents([]); setContentSearch(""); }}
                    style={{ background:'white', padding:'6px 16px', color:'#551FFF', border:'1px solid #551FFF', borderRadius:'5px', cursor:'pointer', fontSize:'12px' }}>취소</button>
                  <button disabled={(!newPlaylistName.trim() && !pendingPlaylistId) || (uploadFiles.length === 0 && selectedExistingContents.length === 0) || creating}
                    onClick={async () => {
                      const totalItems = uploadFiles.length + selectedExistingContents.length;
                      if (totalItems === 0) return;
                      setCreating(true);
                      try {
                        const contentItems = [];

                        // 1. 새 파일 업로드
                        for (const file of uploadFiles) {
                          const formData = new FormData();
                          formData.append('content', file);
                          formData.append('name', file.name);
                          formData.append('userId', user?._id);
                          formData.append('role', user?.role || 'admin');
                          const res = await api.post('/content/upload', formData);
                          const c = res.data?.content;
                          if (c?._id) contentItems.push({ id: c._id, type: c.type, url: c.url, size: c.size, duration: 10 });
                        }

                        // 2. 기존 콘텐츠 추가
                        for (const c of selectedExistingContents) {
                          contentItems.push({ id: c._id, type: c.type, url: c.url, size: c.size, duration: 10 });
                        }

                        // 3. 플레이리스트: 기존 또는 새로 생성
                        let targetPlId = pendingPlaylistId;
                        if (!targetPlId) {
                          const plRes = await api.post('/playlist/add', {
                            name: newPlaylistName.trim(),
                            user: user?._id,
                            role: user?.role || 'admin',
                          });
                          targetPlId = plRes.data?.playlist?._id;
                        }
                        if (!targetPlId) throw new Error('플레이리스트 생성 실패');

                        // 4. 콘텐츠를 플레이리스트에 추가
                        if (contentItems.length > 0) {
                          await api.patch(`/playlist/addcontent/${targetPlId}`, { content: contentItems });
                        }

                        // 5. 현재 스크린에 적용
                        dispatch(addPlaylistToScreen({ id: params.id, value: targetPlId }));
                        dispatch(getPlaylist(user?._id));
                        dispatch(getContent(user?._id));
                        setTimeout(() => {
                          socketRef.current?.emit("playlist_added", { detail: { mac: screens?.find(s => s._id === params.id)?.mac } });
                        }, 1500);

                        setOpenPlaylistDialog(false); setQuickCreateMode(false);
                        setUploadFiles([]); setNewPlaylistName(""); setPendingPlaylistId("");
                        setSelectedExistingContents([]); setContentSearch("");
                        toast?.success?.(`${contentItems.length}개 콘텐츠 적용 완료`);
                      } catch (err) {
                        console.error('Quick create failed:', err);
                        toast?.error?.('실패: ' + (err.response?.data || err.message || '서버 오류'));
                      }
                      setCreating(false);
                    }}
                    style={{ background: (uploadFiles.length + selectedExistingContents.length) > 0 && !creating ? '#551FFF' : '#ccc',
                      padding:'6px 20px', color:'white', border:'none', borderRadius:'5px', fontSize:'12px', fontWeight:600,
                      cursor: (uploadFiles.length + selectedExistingContents.length) > 0 && !creating ? 'pointer' : 'default' }}>
                    {creating ? '처리 중...' : pendingPlaylistId
                      ? `콘텐츠 추가 (${uploadFiles.length + selectedExistingContents.length}개)`
                      : `생성 및 적용 (${uploadFiles.length + selectedExistingContents.length}개)`}
                  </button>
                </Box>
              </Box>
            )}

            {/* 새 플레이리스트 만들기 버튼 (목록이 있을 때도 표시) */}
            {!quickCreateMode && (
              <Box sx={{ textAlign:'center', mt:1 }}>
                <button onClick={() => { setQuickCreateMode(true); setNewPlaylistName(screens?.find(s => s._id === params.id)?.name || ''); }}
                  style={{ background:'none', color:'#551FFF', border:'1px dashed #551FFF', borderRadius:'6px', padding:'6px 16px', cursor:'pointer', fontSize:'12px' }}>
                  + 새 플레이리스트 만들기
                </button>
              </Box>
            )}

            <Box sx={{ display:'flex', justifyContent:'center', gap:1, mt:1 }}>
              <button onClick={() => { setOpenPlaylistDialog(false); setPlaylistSearch(""); setPendingPlaylistId(""); setQuickCreateMode(false); setUploadFiles([]); }} style={{ background:'white', padding:'8px', width:'130px', color:'#551FFF', border:'1px solid #551FFF', borderRadius:'5px', cursor:'pointer' }}>취소</button>
              <button onClick={() => { if (pendingPlaylistId) selectPlaylistById(pendingPlaylistId); setPendingPlaylistId(""); }} disabled={!pendingPlaylistId} style={{ background: pendingPlaylistId ? '#551FFF' : '#ccc', padding:'8px', width:'130px', color:'white', border:'none', borderRadius:'5px', cursor: pendingPlaylistId ? 'pointer' : 'default' }}>적용</button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* -M/_M 저장 시: 기존 할당 플레이리스트 제거 여부 */}
      <Dialog open={openNameSyncDialog} onClose={() => { setOpenNameSyncDialog(false); setPendingNameSave(null); }}>
        <DialogTitle sx={{ fontWeight:700, color:'#551FFF' }}>플레이리스트 할당 해제</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize:13, color:'#555' }}>
            스크린 이름이 -M/_M으로 끝나 연동이 해제되었습니다. 기존에 할당된 플레이리스트를 어떻게 할까요?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2, flexWrap:'wrap', gap:1 }}>
          <Button onClick={() => handleNameSyncDialogChoice('unassign')} variant="outlined" sx={{ borderColor:'#551FFF', color:'#551FFF' }}>할당만 해제</Button>
          <Button onClick={() => handleNameSyncDialogChoice('delete')} variant="contained" color="error">할당 해제 후 플레이리스트 삭제</Button>
          <Button onClick={() => { setOpenNameSyncDialog(false); setPendingNameSave(null); }} sx={{ color:'#888' }}>유지</Button>
        </DialogActions>
      </Dialog>

      {/* 스크린샷 풀사이즈 미리보기 */}
      {screenshotPreview && screenshot && (
        <div
          onClick={() => setScreenshotPreview(false)}
          style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, cursor:'zoom-out' }}
        >
          <img
            src={`data:image/png;base64,${screenshot}`}
            alt="screenshot-full"
            style={{ maxWidth:'92vw', maxHeight:'92vh', objectFit:'contain', borderRadius:8, boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}
          />
        </div>
      )}
    </>
  );
}

export default Setting;
